import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Lock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const inputCls = "mt-3 w-full rounded-xl border border-inkline bg-parchment px-4 py-3 text-sm outline-none focus:border-spice";

/** The owner's permanent private link opens this sign-in form (email + password). Nothing else is emailed. */
export default function AdminEnter() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState(undefined);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.post("/auth/enter", { token })
      .then(() => setValid(true))
      .catch((err) => { setValid(false); setError(formatApiError(err)); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { token, ...form });
      toast.success(`Welcome back, ${data.name}.`);
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="grain flex min-h-[70vh] items-center justify-center bg-parchment px-4 pt-32 pb-20" data-testid="admin-enter">
      <div className="w-full max-w-md rounded-3xl border border-inkline bg-cream p-8 sm:p-10">
        {valid === undefined && <Loader2 size={36} className="mx-auto animate-spin text-spice" />}
        {valid === false && (
          <div className="text-center">
            <XCircle size={36} className="mx-auto text-spice" />
            <h1 className="mt-6 font-display text-3xl font-semibold text-ink" data-testid="admin-enter-error">Link didn't work</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted2">{error}</p>
            <button onClick={() => navigate("/admin", { replace: true })} className="mt-8 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-parchment transition-colors hover:bg-spice" data-testid="admin-enter-retry">Request my link again</button>
          </div>
        )}
        {valid && (
          <form onSubmit={submit} data-testid="admin-login-form">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ink"><Lock size={22} className="text-saffron" /></span>
            <h1 className="mt-6 font-display text-3xl font-semibold text-ink">Kitchen sign-in</h1>
            <p className="mt-2 text-sm text-muted2">Your private link checked out. Enter your admin email and password.</p>
            <input type="email" autoComplete="username" required placeholder="Admin email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="admin-email" />
            <input type="password" autoComplete="current-password" required placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="admin-password" />
            <button type="submit" disabled={busy} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-spice disabled:opacity-60" data-testid="admin-login-submit">
              {busy && <Loader2 size={15} className="animate-spin" />} Sign In
            </button>
            <p className="mt-4 text-center text-xs text-muted2">Bookmark this page — it's your permanent door to the dashboard.</p>
          </form>
        )}
      </div>
    </main>
  );
}
